import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { UserDto, UserStatus } from '../../config/userConfig'
import { DropdownConnectProps } from './Connect/DropdownConnect'
import useUser from '../../hooks/useUser'
import useUsers from '../../hooks/useUsers'
import BaseDropdown, { ComboBoxOption } from './BaseDropdown'

export enum UserDropdownKey {
    Id = 'id',
    Email = 'email',
}

export type UserDropdownMapperProps = {
    enabledOnly?: boolean // Only show enabled users.
    showHidden?: boolean // Show hidden users.
    userKey?: UserDropdownKey
    filter?: (user: UserDto) => boolean
}

type Props = UserDropdownMapperProps & DropdownConnectProps<UserDto>

export default function LegacyUserDropdown(props: Props): ReactElement {
    const { users } = useUsers()
    const currentUser = useUser()

    const options = useMemo<ComboBoxOption<UserDto>[]>(() => {
        const key = props.userKey || UserDropdownKey.Id
        const getSelectedKeys = selectedKey =>
            selectedKey ? (Array.isArray(selectedKey) ? selectedKey : [selectedKey]) : []
        const filter = (user: UserDto): boolean =>
            // Always keep the selected options, and options that are the current user.
            getSelectedKeys(props.selectedKey).includes(user[key]) || user.id === currentUser.id

        return (
            (users || [])
                // By default, it will not show hidden users.
                .filter(user => props.showHidden || user.status !== UserStatus.Hidden || filter(user))
                // By default, it will only show enabled users.
                .filter(user => !(props.enabledOnly ?? true) || user.status !== UserStatus.Disabled || filter(user))
                .filter(user => (props.filter ? props.filter(user) || filter(user) : true))
                .sort((a: UserDto, b: UserDto) => a.fullName.localeCompare(b.fullName))
                .map<ComboBoxOption<UserDto>>(user => ({
                    key: user[key],
                    text: user.fullName,
                    data: user,
                }))
        )
    }, [users, currentUser, props])

    return <BaseDropdown<UserDto> placeholder="Selecteer gebruiker" {...props} options={options} />
}
